import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Plus, ClipboardCheck, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { QCTemplateCard } from '../../components/admin/qc/QCTemplateCard';
import {
  QCTemplateDialog,
  QCItemDialog,
  QCDeleteDialog,
} from '../../components/admin/qc/QCDialogs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminQCTemplatesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/dashboard');
  const [templates, setTemplates] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [tplDialog, setTplDialog] = useState({ open: false, editing: null });
  const [tplForm, setTplForm] = useState({ name: '', applies_to_service_id: '', is_default: false });

  const [itemDialog, setItemDialog] = useState({ open: false, editing: null, templateId: null });
  const [itemForm, setItemForm] = useState({ label: '', is_required: true });

  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, target: null, templateId: null });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [tplRes, svcRes] = await Promise.all([
        axios.get(`${API_URL}/api/qc-templates`, { headers }),
        axios.get(`${API_URL}/api/services-list`, { headers }),
      ]);
      setTemplates(tplRes.data || []);
      setServices(svcRes.data || []);
    } catch (_) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getServiceName = (serviceId) => {
    if (!serviceId) return null;
    const svc = services.find(s => s.id === serviceId);
    return svc?.name || 'Unknown';
  };

  // Template CRUD
  const openCreateTemplate = () => {
    setTplForm({ name: '', applies_to_service_id: '', is_default: false });
    setTplDialog({ open: true, editing: null });
  };

  const openEditTemplate = (tpl) => {
    setTplForm({ name: tpl.name, applies_to_service_id: tpl.applies_to_service_id || '', is_default: tpl.is_default });
    setTplDialog({ open: true, editing: tpl });
  };

  const handleSaveTemplate = async () => {
    if (!tplForm.name.trim()) { toast.error('Template name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: tplForm.name,
        applies_to_service_id: tplForm.applies_to_service_id || null,
        is_default: tplForm.is_default,
      };
      if (tplDialog.editing) {
        await axios.put(`${API_URL}/api/qc-templates/${tplDialog.editing.id}`, payload, { headers });
        toast.success('Template updated');
      } else {
        await axios.post(`${API_URL}/api/qc-templates`, payload, { headers });
        toast.success('Template created');
      }
      setTplDialog({ open: false, editing: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deleteDialog.target) return;
    try {
      await axios.delete(`${API_URL}/api/qc-templates/${deleteDialog.target.id}`, { headers });
      toast.success('Template deleted');
      setDeleteDialog({ open: false, type: null, target: null, templateId: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleToggleActive = async (tpl) => {
    try {
      await axios.put(`${API_URL}/api/qc-templates/${tpl.id}`, { is_active: !tpl.is_active }, { headers });
      toast.success(tpl.is_active ? 'Template deactivated' : 'Template activated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  // Item CRUD
  const openAddItem = (templateId) => {
    setItemForm({ label: '', is_required: true });
    setItemDialog({ open: true, editing: null, templateId });
  };

  const openEditItem = (item, templateId) => {
    setItemForm({ label: item.label, is_required: item.is_required });
    setItemDialog({ open: true, editing: item, templateId });
  };

  const handleSaveItem = async () => {
    if (!itemForm.label.trim()) { toast.error('Item label is required'); return; }
    setSaving(true);
    const templateId = itemDialog.templateId;
    try {
      const tpl = templates.find(t => t.id === templateId);
      const sortOrder = itemDialog.editing ? itemDialog.editing.sort_order : (tpl?.items?.length || 0);
      const payload = {
        qc_template_id: templateId,
        label: itemForm.label,
        sort_order: sortOrder,
        is_required: itemForm.is_required,
      };
      if (itemDialog.editing) {
        await axios.put(`${API_URL}/api/qc-templates/${templateId}/items/${itemDialog.editing.id}`, payload, { headers });
        toast.success('Item updated');
      } else {
        await axios.post(`${API_URL}/api/qc-templates/${templateId}/items`, payload, { headers });
        toast.success('Item added');
      }
      setItemDialog({ open: false, editing: null, templateId: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!deleteDialog.target || !deleteDialog.templateId) return;
    try {
      await axios.delete(`${API_URL}/api/qc-templates/${deleteDialog.templateId}/items/${deleteDialog.target.id}`, { headers });
      toast.success('Item deleted');
      setDeleteDialog({ open: false, type: null, target: null, templateId: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleToggleRequired = async (item, templateId, isRequired) => {
    try {
      await axios.put(`${API_URL}/api/qc-templates/${templateId}/items/${item.id}`, {
        qc_template_id: templateId, label: item.label, sort_order: item.sort_order, is_required: isRequired,
      }, { headers });
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleReorder = async (templateId, newOrder) => {
    setTemplates(prev => prev.map(t => t.id === templateId ? { ...t, items: newOrder } : t));
    try {
      await axios.put(`${API_URL}/api/qc-templates/${templateId}/items/reorder`,
        newOrder.map(i => i.id), { headers });
    } catch (err) {
      toast.error('Failed to reorder');
      fetchData();
    }
  };

  const assignedServiceIds = templates.filter(t => t.applies_to_service_id).map(t => t.applies_to_service_id);
  const unassignedServices = services.filter(s => !assignedServiceIds.includes(s.id));

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" /></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between min-h-16 py-3 gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <Button variant="ghost" size="sm" onClick={back} data-testid="back-to-dashboard">
                <ArrowLeft className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Dashboard</span>
              </Button>
              <h1 className="text-xl font-bold text-zinc-900 truncate">QC Templates</h1>
              <Badge variant="secondary" className="text-xs">{templates.length}</Badge>
            </div>
            <Button
              size="sm"
              onClick={openCreateTemplate}
              className="bg-[#F56F05] hover:bg-[#C2410C] text-white"
              data-testid="create-template-btn"
            >
              <Plus className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">New Template</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {unassignedServices.length > 0 && (
          <Card className="border-0 shadow-sm border-l-4 border-l-yellow-400 bg-yellow-50/50" data-testid="unassigned-warning">
            <CardContent className="p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Services using default template:</p>
                <p className="text-xs text-yellow-600 mt-0.5">
                  {unassignedServices.map(s => s.name).join(', ')} — assign a specific template for more precise checklists.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {templates.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="py-16 text-center">
              <ClipboardCheck className="h-12 w-12 text-zinc-200 mx-auto mb-4" />
              <p className="text-zinc-500 font-medium">No QC templates yet</p>
              <p className="text-sm text-zinc-400 mt-1">Create your first template to define quality checklists for cleaners.</p>
              <Button className="mt-4 bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={openCreateTemplate}>
                <Plus className="h-4 w-4 mr-1" /> Create Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {templates.map(tpl => (
              <QCTemplateCard
                key={tpl.id}
                tpl={tpl}
                isExpanded={expandedId === tpl.id}
                onToggleExpand={() => setExpandedId(expandedId === tpl.id ? null : tpl.id)}
                onEdit={openEditTemplate}
                onDelete={(t) => setDeleteDialog({ open: true, type: 'template', target: t, templateId: null })}
                onToggleActive={handleToggleActive}
                onEditItem={openEditItem}
                onDeleteItem={(item, templateId) => setDeleteDialog({ open: true, type: 'item', target: item, templateId })}
                onToggleRequired={handleToggleRequired}
                onReorder={handleReorder}
                onAddItem={openAddItem}
                serviceName={getServiceName(tpl.applies_to_service_id)}
              />
            ))}
          </div>
        )}
      </main>

      <QCTemplateDialog
        open={tplDialog.open}
        onClose={() => setTplDialog({ open: false, editing: null })}
        onSave={handleSaveTemplate}
        editing={tplDialog.editing}
        form={tplForm}
        setForm={setTplForm}
        services={services}
        saving={saving}
      />

      <QCItemDialog
        open={itemDialog.open}
        onClose={() => setItemDialog({ open: false, editing: null, templateId: null })}
        onSave={handleSaveItem}
        editing={itemDialog.editing}
        form={itemForm}
        setForm={setItemForm}
        saving={saving}
      />

      <QCDeleteDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, target: null, templateId: null })}
        onConfirm={deleteDialog.type === 'template' ? handleDeleteTemplate : handleDeleteItem}
        type={deleteDialog.type}
        target={deleteDialog.target}
      />
    </div>
  );
};

export default AdminQCTemplatesPage;
